import Decimal from 'decimal.js';
import { foldVi } from './master-data';
import { PERCENT_DP, PRICE_DP, QTY_DP } from './quote-calc';

/**
 * Báo giá dự toán (P05.6 – BOQ công trình PCCC / HVAC / ME; DEC-457, docs/phases/P05.6/bao-gia-du-toan.md).
 * Chứng từ RIÊNG, không nằm trong danh sách / luồng báo giá thường; mỗi phiếu gắn 1 cơ hội trên Pipeline (nhãn "Dự toán").
 * Dòng = hạng mục BOQ theo hệ: vật tư (giá bán) + nhân công (đơn giá thi công), VAT theo dòng, tiền chưa VAT là chính (DEC-005).
 */
export const ESTIMATE_CODE_PREFIX = 'BQ';

export const ESTIMATE_STATUSES = ['draft', 'sent', 'won', 'lost', 'cancelled'] as const;
export type EstimateStatus = (typeof ESTIMATE_STATUSES)[number];
export const ESTIMATE_STATUS_LABELS: Record<EstimateStatus, string> = {
  draft: 'Đang lập',
  sent: 'Đã gửi khách',
  won: 'Khách chốt',
  lost: 'Không thành',
  cancelled: 'Đã hủy',
};
/** Đổi trạng thái bằng POST /estimates/:id/status. Gửi lại sau khi sửa: sent → draft (Sửa lại) → sent. */
export const ESTIMATE_TRANSITIONS: Record<EstimateStatus, readonly EstimateStatus[]> = {
  draft: ['sent', 'cancelled'],
  sent: ['draft', 'won', 'lost', 'cancelled'],
  won: [],
  lost: ['draft'],
  cancelled: [],
};
export const isEstimateEditable = (s: EstimateStatus) => s === 'draft';

/** Hệ thống của dòng BOQ. 'other' = chi phí chung (vận chuyển, lắp đặt chung, quản lý…). */
export const ESTIMATE_SYSTEMS = ['pccc', 'hvac', 'me', 'other'] as const;
export type EstimateSystem = (typeof ESTIMATE_SYSTEMS)[number];
export const ESTIMATE_SYSTEM_LABELS: Record<EstimateSystem, string> = {
  pccc: 'PCCC',
  hvac: 'HVAC',
  me: 'ME (điện, nước)',
  other: 'Chi phí chung',
};
/** Hạng mục gợi ý theo hệ (người lập gõ tự do được, đây chỉ là danh sách chọn nhanh). */
export const ESTIMATE_SECTION_SUGGESTIONS: Record<EstimateSystem, readonly string[]> = {
  pccc: [
    'Hệ chữa cháy vách tường',
    'Hệ chữa cháy tự động Sprinkler',
    'Hệ chữa cháy ngoài nhà',
    'Trạm bơm chữa cháy',
    'Hệ báo cháy tự động',
    'Bình chữa cháy, phương tiện ban đầu',
    'Đèn chiếu sáng sự cố, đèn exit',
    'Hệ hút khói, tăng áp',
  ],
  hvac: ['Hệ điều hòa VRV / VRF', 'Hệ Chiller', 'Ống gió, miệng gió', 'Quạt thông gió, hút khói', 'Ống đồng, ống nước ngưng', 'Bảo ôn'],
  me: ['Hệ cấp nước', 'Hệ thoát nước', 'Tủ điện, cáp điện', 'Chiếu sáng, ổ cắm', 'Máng cáp, thang cáp', 'Chống sét, tiếp địa'],
  other: ['Vận chuyển', 'Chi phí chung', 'Thử áp, nghiệm thu'],
};

/** Nguồn của dòng: nhập tay / Excel / AI đọc PDF hoặc ảnh / bóc từ bản vẽ DXF. */
export const ESTIMATE_LINE_SOURCES = ['manual', 'excel', 'pdf', 'image', 'dxf'] as const;
export type EstimateLineSource = (typeof ESTIMATE_LINE_SOURCES)[number];
export const ESTIMATE_LINE_SOURCE_LABELS: Record<EstimateLineSource, string> = {
  manual: 'Nhập tay',
  excel: 'Excel',
  pdf: 'AI đọc PDF',
  image: 'AI đọc ảnh',
  dxf: 'Bóc bản vẽ DXF',
};

/** Độ tin cậy dòng AI / bóc bản vẽ: 'low' và 'medium' tô vàng tới khi người lập sửa / xác nhận dòng. */
export const ESTIMATE_CONFIDENCES = ['high', 'medium', 'low'] as const;
export type EstimateConfidence = (typeof ESTIMATE_CONFIDENCES)[number];

export const ESTIMATE_MAX_LINES = 2000;

/** Phương án PA1…PA5 trên cùng phiếu ("Cho đại diện và PA", anh Tiến 24/10/2026): mỗi dòng thuộc 1 PA, khách chốt 1 PA. */
export const ESTIMATE_MAX_OPTIONS = 5;
export const estimateOptionLabel = (no: number) => `PA${no}`;

const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
const dec = (v: number | string | null | undefined, dp: number) => (v === null || v === undefined || v === '' ? new D(0) : new D(v).toDecimalPlaces(dp));
const vnd = (v: Decimal) => v.toDecimalPlaces(0).toNumber();

export interface EstimateLineInput {
  quantity: number | string;
  materialPrice: number | string;
  laborPrice: number | string;
  vatRate: number | string;
  /** Giá vốn vật tư / 1 ĐVT (chỉ người có price.view_cost) */
  costPrice?: number | string | null;
}

export interface EstimateLineAmounts {
  materialAmount: number;
  laborAmount: number;
  /** Thành tiền chưa VAT = vật tư + nhân công */
  netAmount: number;
  vatAmount: number;
  totalAmount: number;
  costAmount: number | null;
}

/**
 * Tiền 1 dòng dự toán (VND, ROUND_HALF_UP, làm tròn theo dòng như quote-calc):
 *   vật tư = round(SL × đơn giá vật tư); nhân công = round(SL × đơn giá nhân công)
 *   chưa VAT = vật tư + nhân công; VAT = round(chưa VAT × VAT% / 100); giá vốn = round(SL × giá vốn)
 */
export function calcEstimateLine(l: EstimateLineInput): EstimateLineAmounts {
  const qty = dec(l.quantity, QTY_DP);
  const material = qty.mul(dec(l.materialPrice, PRICE_DP)).toDecimalPlaces(0);
  const labor = qty.mul(dec(l.laborPrice, PRICE_DP)).toDecimalPlaces(0);
  const net = material.plus(labor);
  const vat = net.mul(dec(l.vatRate, PERCENT_DP)).div(100).toDecimalPlaces(0);
  const hasCost = l.costPrice !== null && l.costPrice !== undefined && l.costPrice !== '';
  return {
    materialAmount: vnd(material),
    laborAmount: vnd(labor),
    netAmount: vnd(net),
    vatAmount: vnd(vat),
    totalAmount: vnd(net.plus(vat)),
    costAmount: hasCost ? vnd(qty.mul(dec(l.costPrice, PRICE_DP))) : null,
  };
}

export interface EstimateTotals {
  materialTotal: number;
  laborTotal: number;
  netTotal: number;
  vatTotal: number;
  grandTotal: number;
  /** Giá vốn vật tư các dòng có giá vốn */
  costTotal: number;
  /** Lãi vật tư = tiền vật tư − giá vốn, chỉ trên dòng có giá vốn */
  materialProfit: number;
  linesMissingCost: number;
}

export function calcEstimateTotals(lines: EstimateLineInput[]): EstimateTotals {
  let material = new D(0);
  let labor = new D(0);
  let vat = new D(0);
  let cost = new D(0);
  let materialWithCost = new D(0);
  let missing = 0;
  for (const l of lines) {
    const a = calcEstimateLine(l);
    material = material.plus(a.materialAmount);
    labor = labor.plus(a.laborAmount);
    vat = vat.plus(a.vatAmount);
    if (a.costAmount === null) missing++;
    else {
      cost = cost.plus(a.costAmount);
      materialWithCost = materialWithCost.plus(a.materialAmount);
    }
  }
  const net = material.plus(labor);
  return {
    materialTotal: vnd(material),
    laborTotal: vnd(labor),
    netTotal: vnd(net),
    vatTotal: vnd(vat),
    grandTotal: vnd(net.plus(vat)),
    costTotal: vnd(cost),
    materialProfit: vnd(materialWithCost.minus(cost)),
    linesMissingCost: missing,
  };
}

/** Cộng tiền theo hệ (bảng tổng hợp đầu phiếu / PDF): thứ tự PCCC → HVAC → ME → Chi phí chung, chỉ hệ có dòng. */
export function totalsBySystem<T extends EstimateLineInput & { system: EstimateSystem }>(lines: T[]) {
  return ESTIMATE_SYSTEMS.flatMap((system) => {
    const ls = lines.filter((l) => l.system === system);
    if (!ls.length) return [];
    const t = calcEstimateTotals(ls);
    return [
      {
        system,
        lineCount: ls.length,
        materialTotal: t.materialTotal,
        laborTotal: t.laborTotal,
        netTotal: t.netTotal,
        vatTotal: t.vatTotal,
        grandTotal: t.grandTotal,
      },
    ];
  });
}

// ---------------------------------------------------------------------------
// File bản vẽ / BOQ đính kèm phiếu (B2): tải lên, AI đọc PDF / ảnh, bóc khối lượng DXF, đọc Excel → dòng nháp
// ---------------------------------------------------------------------------
export const ESTIMATE_FILE_KINDS = ['pdf', 'dxf', 'dwg', 'image', 'excel', 'other'] as const;
export type EstimateFileKind = (typeof ESTIMATE_FILE_KINDS)[number];
export const ESTIMATE_FILE_KIND_LABELS: Record<EstimateFileKind, string> = {
  pdf: 'PDF',
  dxf: 'CAD (DXF)',
  dwg: 'CAD (DWG)',
  image: 'Ảnh',
  excel: 'Excel / CSV',
  other: 'Khác',
};
/** Giới hạn 1 file: PDF / DXF / DWG 100 MB (khớp CHECK estimate_files.size_bytes; DXF lưu nén), ảnh / Excel / khác 30 MB */
export const ESTIMATE_FILE_MAX_BYTES = 100 * 1024 * 1024;
export const ESTIMATE_FILE_MAX_BYTES_SMALL = 30 * 1024 * 1024;
export const estimateFileMaxBytes = (k: EstimateFileKind) =>
  k === 'pdf' || k === 'dxf' || k === 'dwg' ? ESTIMATE_FILE_MAX_BYTES : ESTIMATE_FILE_MAX_BYTES_SMALL;
export const ESTIMATE_MAX_FILES = 30;
/** Mỗi lần gửi AI tối đa ~18 MB và 50 trang: PDF lớn hơn thì ERP tự chia phần, đọc từng phần rồi gộp dòng trùng */
export const ESTIMATE_AI_CHUNK_BYTES = 18 * 1024 * 1024;
export const ESTIMATE_AI_CHUNK_PAGES = 50;
/** Loại file đọc được ra dòng nháp (DXF / DWG bóc khối lượng không tốn token, PDF / ảnh qua AI, Excel như BOQ) */
export const isEstimateFileReadable = (k: EstimateFileKind) => k === 'pdf' || k === 'image' || k === 'dxf' || k === 'dwg' || k === 'excel';

/** Nhận loại file theo đuôi (rồi mới đến MIME): trình duyệt hay gửi DXF / DWG là application/octet-stream. */
export function estimateFileKind(fileName: string, mime?: string | null): EstimateFileKind {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'dxf') return 'dxf';
  if (ext === 'dwg') return 'dwg';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return 'image';
  if (['xlsx', 'xlsm', 'xls', 'csv'].includes(ext)) return 'excel';
  const m = (mime ?? '').toLowerCase();
  if (m === 'application/pdf') return 'pdf';
  if (m.startsWith('image/')) return 'image';
  return 'other';
}

const SYSTEM_KEYWORDS: [EstimateSystem, RegExp][] = [
  [
    'pccc',
    /\b(pccc|chua chay|bao chay|sprinkler|sprinkle|spk|drencher|hydrant|tru nuoc|lang phun|cuon voi|binh chua|fire|fa|ff|fp|fps|fm200|novec|bom chua|tu chua|den exit|exit|su co|bao khoi|dau bao|deluge|tang ap|hut khoi)\b/,
  ],
  [
    'hvac',
    /\b(hvac|acmv|dieu hoa|vrv|vrf|chiller|ahu|fcu|pau|erv|ong gio|duct|mieng gio|grille|diffuser|damper|van gio|quat|fan|ong dong|nuoc ngung|gas lanh|bao on|cassette|dan nong|dan lanh|cooling)\b/,
  ],
  [
    'me',
    /\b(mep|dien|cap dien|cable|day dien|den led|den chieu sang|chieu sang|o cam|cong tac|tu dien|mcb|mccb|acb|mang cap|thang cap|tray|ladder|chong set|tiep dia|cap nuoc|thoat nuoc|plumbing|ppr|upvc|hdpe|pvc|ve sinh|lavabo|bon cau|be nuoc|bom cap|elec|plumb)\b/,
  ],
];

/**
 * Đoán hệ của dòng / layer CAD theo từ khóa (bỏ dấu). Thứ tự ưu tiên PCCC → HVAC → ME (ống thép trong hệ chữa cháy là PCCC).
 * Không đoán được → null (nơi gọi dùng hệ người chọn khi đọc file, hoặc "Chi phí chung").
 */
export function guessEstimateSystem(...texts: (string | null | undefined)[]): EstimateSystem | null {
  const s = foldVi(
    texts
      .filter(Boolean)
      .join(' ')
      .replace(/[_\-.]+/g, ' '),
  );
  if (!s) return null;
  for (const [sys, re] of SYSTEM_KEYWORDS) if (re.test(s)) return sys;
  return null;
}

// ---------------------------------------------------------------------------
// Gộp dòng trùng (gom tầng): cùng hệ, cùng vật tư, cùng ĐVT, cùng đơn giá → 1 dòng, cộng khối lượng
// ---------------------------------------------------------------------------
const FLOOR_RE =
  /(?:^|[\s,;:(\-–—/])((?:tầng|tang|lầu|lau|floor|level|tầng hầm|hầm|ham|basement|mái|mai|roof)\s*[\w.]*|(?:B|L|T)\d{1,2})(?=$|[\s,;:)\-–—/])/giu;

/** Nhãn tầng trong hạng mục / ghi chú ("Tầng 2 – Sprinkler" → "Tầng 2"); không có → null */
export function estimateFloorLabel(s: string | null | undefined): string | null {
  if (!s) return null;
  FLOOR_RE.lastIndex = 0;
  const m = FLOOR_RE.exec(s);
  return m ? m[1]!.trim() : null;
}

/** Hạng mục bỏ phần tầng ("Tầng 2 – Hệ Sprinkler" → "Hệ Sprinkler") để gom các tầng cùng hạng mục */
export function stripEstimateFloor(s: string | null | undefined): string | null {
  if (!s) return null;
  const out = s
    .replace(FLOOR_RE, ' ')
    .replace(/^[\s,;:\-–—/]+|[\s,;:\-–—/]+$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return out || null;
}

export interface EstimateMergeable {
  system: EstimateSystem;
  section?: string | null;
  productId?: number | null;
  itemName: string;
  spec?: string | null;
  brand?: string | null;
  unit: string;
  materialPrice: number;
  laborPrice: number;
  vatRate: number;
}

/** Khóa gộp: hệ + hạng mục (bỏ tầng) + mã (hoặc tên + quy cách + hãng) + ĐVT + đơn giá vật tư / nhân công + VAT */
export function estimateMergeKey(l: EstimateMergeable): string {
  const item = l.productId ? `#${l.productId}` : [foldVi(l.itemName), foldVi(l.spec), foldVi(l.brand)].join('|');
  return [l.system, foldVi(stripEstimateFloor(l.section)), item, foldVi(l.unit), Number(l.materialPrice), Number(l.laborPrice), Number(l.vatRate)].join('§');
}

/**
 * Gộp dòng trùng giữ thứ tự dòng đầu tiên; ghi chú dòng gộp ghi số từng phần ("Tầng 1: 120 · Tầng 2: 98", hoặc theo trang).
 * part(l): nhãn phần của dòng (tầng / trang); qty / setQty: đọc / ghi khối lượng; merged(first, group) tùy biến dòng kết quả.
 */
export function mergeEstimateLines<T extends EstimateMergeable>(
  lines: T[],
  opts: { qty: (l: T) => number; part?: (l: T, i: number) => string | null; build: (first: T, total: number, note: string | null, group: T[]) => T },
): { lines: T[]; merged: number } {
  const groups = new Map<string, { idx: number; items: { l: T; i: number }[] }>();
  lines.forEach((l, i) => {
    const k = estimateMergeKey(l);
    const g = groups.get(k);
    if (g) g.items.push({ l, i });
    else groups.set(k, { idx: i, items: [{ l, i }] });
  });
  let merged = 0;
  const out = [...groups.values()]
    .sort((a, b) => a.idx - b.idx)
    .map(({ items }) => {
      if (items.length === 1) return items[0]!.l;
      merged += items.length - 1;
      const total = new Decimal(items.reduce((s, x) => s.plus(opts.qty(x.l)), new Decimal(0))).toDecimalPlaces(QTY_DP).toNumber();
      const parts = items.map((x) => [opts.part?.(x.l, x.i) ?? null, opts.qty(x.l)] as const);
      const note = parts.every(([p]) => p)
        ? parts.map(([p, q]) => `${p}: ${q}`).join(' · ')
        : `Gộp ${items.length} dòng: ${parts.map(([, q]) => q).join(' + ')}`;
      const first = items[0]!.l;
      return opts.build(
        { ...first, section: stripEstimateFloor(first.section) ?? first.section ?? null },
        total,
        note,
        items.map((x) => x.l),
      );
    });
  return { lines: out, merged };
}

/** Ước token AI mỗi trang PDF / ảnh bản vẽ (ảnh trang + chữ); đọc trên ngưỡng trang thì hỏi xác nhận trước */
export const ESTIMATE_AI_TOKENS_PER_PAGE = 2500;
export const ESTIMATE_AI_CONFIRM_PAGES = 20;

/** "3-8, 12" → [3,4,5,6,7,8,12] (trong 1…max, bỏ trùng, tăng dần). Trống → null (đọc hết). Sai cú pháp → ném lỗi. */
export function parsePageRanges(input: string | null | undefined, max: number): number[] | null {
  const s = (input ?? '').trim();
  if (!s) return null;
  const out = new Set<number>();
  for (const part of s.split(/[,;\s]+/).filter(Boolean)) {
    const m = /^(\d+)(?:\s*[-–]\s*(\d+))?$/.exec(part);
    if (!m) throw new Error(`Trang "${part}" không hợp lệ: gõ kiểu 3-8, 12`);
    const a = Number(m[1]);
    const b = m[2] ? Number(m[2]) : a;
    if (a < 1 || b < a || b > max) throw new Error(`Trang ${part} ngoài khoảng 1–${max}`);
    for (let i = a; i <= b; i++) out.add(i);
  }
  return [...out].sort((x, y) => x - y);
}

// ------------------------------------------------------------------ Bóc bản vẽ CAD (DXF / DWG): chọn dòng trước khi đưa vào BOQ

/** Dòng bóc từ bản vẽ CAD, chưa vào BOQ: người lập tích chọn rồi mới thêm */
export interface CadTakeoffRow {
  /** Khóa ổn định giữa lần bóc thử và lần thêm: loại|layer|block */
  key: string;
  layer: string;
  kind: 'count' | 'length';
  block: string | null;
  quantity: number;
  unit: string;
  system: EstimateSystem | null;
  /** ERP tích sẵn: block thiết bị / tuyến trên layer ống, ống gió, cáp */
  suggested: boolean;
  /** Vì sao không tích sẵn / cần kiểm (null = ổn) */
  reason: string | null;
}

export const cadTakeoffKey = (kind: 'count' | 'length', layer: string, block: string | null) => `${kind}|${layer}|${block ?? ''}`;

/** Layer có tuyến cần đo dài: ống, ống gió, cáp, máng / thang cáp, dây (bỏ dấu, tách theo từ) */
const CAD_RUN_LAYER =
  /\b(ong|pipe|piping|pip|duct|ong gio|cap|cable|cables|tray|mang|thang|wire|wiring|day|sprinkler|sprink|spk|hydrant|chua chay|fire|ff|fp|nuoc|water|drain|thoat|ppr|hdpe|upvc|pvc|chw|cdw|cw|hw|gas|refrigerant|condensate|ngung|lanh|busway|thanh dan|conduit)\b/;
/** Block là khung tên, ký hiệu ghi chú, mũi tên, cao độ… không phải vật tư */
const CAD_NOTE_BLOCK =
  /\b(khung|title|tieu de|logo|north|mui ten|arrow|cao do|level|elev|truc|grid|axis|section|mat cat|chi tiet|detail|ghi chu|note|tag|label|legend|chu thich|ky hieu|symbol|border|frame|revision|rev|stamp|xref|viewport|a0|a1|a2|a3|a4|scale|ti le|ty le|dim|text)\b/;
/** Layer kiến trúc / vật dụng / nét vẽ / PDF dán vào: không phải hệ ME (trừ khi tên có từ khóa ME) */
const CAD_NON_MEP_LAYER =
  /\b(vat dung|noi that|furniture|furn|kien truc|arch|cua|door|window|tuong|wall|cot|column|dam|beam|san|slab|stair|cau thang|pdf\d*|geometry|tpl|bound|hatch|\d*hatch|kich thuoc|dim|ghi chu|ghichu|text|net|sym|line|defpoints)\b/;
/** Layer thiết bị (ký hiệu vẽ bằng nét): đếm block được, nhưng nét trên đó không phải tuyến */
const CAD_DEVICE_LAYER = /\b(thiet bi|thind|tb|device|equip|equipment|symbol|ky hieu)\b/;
/** Layer điện động lực / chiếu sáng: nét thường là tuyến cáp */
const CAD_POWER_LAYER = /\b(dien|elec|electric|dong luc|power|chieu sang|lighting)\b/;
/** Một tuyến dài hơn mức này thường là nét khung / sai đơn vị bản vẽ */
export const CAD_RUN_MAX_M = 5000;

/** Phân loại dòng bóc: tích sẵn hay để người lập tự chọn, kèm lý do. Hệ đoán theo layer + tên block. */
export function classifyCadRow(r: {
  layer: string;
  kind: 'count' | 'length';
  block: string | null;
  quantity: number;
}): Pick<CadTakeoffRow, 'suggested' | 'reason' | 'system'> {
  const system = guessEstimateSystem(r.layer, r.block);
  const layer = foldVi(r.layer);
  const layer0 = layer === '0' || layer === 'defpoints';
  if (r.kind === 'count') {
    const name = r.block ?? '';
    const f = foldVi(name);
    const letters = (f.match(/[a-z]/g) ?? []).length;
    const blockSystem = guessEstimateSystem(name);
    if (/[$*|]/.test(name) || letters < 2 || f.replace(/ /g, '').length < 3) return { suggested: false, reason: 'Tên block lạ (block hệ thống / mã tự sinh)', system };
    if (CAD_NOTE_BLOCK.test(f)) return { suggested: false, reason: 'Block ghi chú / khung tên / ký hiệu, không phải vật tư', system };
    // Block trên layer 0 / layer kiến trúc: thường là ký hiệu người vẽ tự đặt tên (ADASD…), trừ khi tên block nói rõ là thiết bị ME
    if (layer0 && !blockSystem) return { suggested: false, reason: 'Block trên layer 0: thường là ký hiệu vẽ, tên do người vẽ đặt', system };
    if (!system && CAD_NON_MEP_LAYER.test(layer)) return { suggested: false, reason: 'Layer kiến trúc / vật dụng / ghi chú, không phải hệ ME', system };
    return { suggested: true, reason: null, system };
  }
  const run = CAD_RUN_LAYER.test(layer) || (CAD_POWER_LAYER.test(layer) && !CAD_DEVICE_LAYER.test(layer));
  if (!run) return { suggested: false, reason: 'Layer không phải ống / ống gió / cáp (nét vẽ, nét khung…)', system };
  if (r.quantity > CAD_RUN_MAX_M) return { suggested: false, reason: `Dài hơn ${CAD_RUN_MAX_M.toLocaleString('vi-VN')} m: kiểm đơn vị bản vẽ`, system };
  return { suggested: true, reason: null, system };
}

/** Xếp dòng bóc: dòng tích sẵn lên đầu, rồi theo hệ, layer, số lượng giảm dần */
export function sortCadRows<T extends Pick<CadTakeoffRow, 'suggested' | 'system' | 'layer' | 'kind' | 'quantity'>>(rows: T[]): T[] {
  const sys = (s: EstimateSystem | null) => (s ? ESTIMATE_SYSTEMS.indexOf(s) : 99);
  return [...rows].sort(
    (a, b) =>
      Number(b.suggested) - Number(a.suggested) ||
      sys(a.system) - sys(b.system) ||
      a.kind.localeCompare(b.kind) ||
      a.layer.localeCompare(b.layer) ||
      b.quantity - a.quantity,
  );
}
